import { faTrashCanXmark } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Autocomplete,
  BottomNavigation,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch
} from "@mui/material"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useApiGet, useApiPost } from "auth/useApi2"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import PaperBox from "components/PaperBox"
import theme from "css/theme/light"
import { FieldArray, Formik } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { t } from "i18next"
import { deviceValidationSchema } from "pages/systemadmin/pages/devices/components/validationSchema"
import { useContext, useEffect, useRef, useState } from "react"
import { SidebarEquipmentForm } from "../../equipment/components/SidebarEquipmentForm"

const serviceLevels = [
  { name: "Inget ", id: null },
  { name: "80", id: 80 },
  { name: "60", id: 60 },
  { name: "40", id: 40 },
  { name: "20", id: 20 }
]

const groupedData = (data) =>
  data.reduce((groups, item) => {
    const key = item.equipmentType
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(item)
    return groups
  }, {})

export const DeviceForm = ({ onSubmit, editDevice, result, feedback }) => {
  const appContext = useContext(AppContext)
  const initialValues = editDevice
    ? {
        ...editDevice,
        equipments: editDevice.equipments || []
      }
    : {
        deviceId: "",
        internalDeviceId: "",
        deviceTypeId: 1,
        isActive: true,
        equipments: []
      }
  const editMode = editDevice ? true : false

  const formRef = useRef()
  const handleSubmit = async (values) => {
    onSubmit(values)
  }
  const [addEquipmentResult, addEquipment] = useApiPost()
  const [technicalPackagesResult, getTechnicalPackages] = useApiGet({})
  const [equipmentsNotConnectedResult, getEquipmentsNotConnected] = useApiGet()
  const [searchValue, setSearchValue] = useState("")
  const [selectedGroup, setSelectedGroup] = useState("")
  const [addedEquipment, setAddedEquipment] = useState(null)
  const [addedEquipmentId, setAddedEquipmentId] = useState(null)

  useEffect(() => {
    getTechnicalPackages(apiEndpoints.systemadmindeviceregistry.listtechnicalpackage)
    getEquipmentsNotConnected(apiEndpoints.systemadmindeviceregistry.listequipmentsnotconnectedtodevice)
  }, [])

  const handleEquipmentSubmit = (values) => {
    addEquipment(apiEndpoints.systemadmindeviceregistry.addequipment, values)
    setAddedEquipmentId(values.equipmentId)
  }

  useEffect(() => {
    if (addEquipmentResult.status === 2) {
      getEquipmentsNotConnected(apiEndpoints.systemadmindeviceregistry.listequipmentsnotconnectedtodevice)
      setAddedEquipment(addedEquipmentId)
    }
  }, [addEquipmentResult.status, getEquipmentsNotConnected])

  useEffect(() => {
    if (equipmentsNotConnectedResult.status === 2 && addedEquipment !== null) {
      const newList = formRef?.current?.values.equipments ? [...formRef?.current?.values.equipments] : []
      const index = equipmentsNotConnectedResult?.data?.findIndex(
        (item) => item.equipmentId === addedEquipment
      )
      if (index !== -1) {
        if (equipmentsNotConnectedResult?.data[index]) {
          equipmentsNotConnectedResult.data[index].mountedOnDeviceId = formRef?.current?.values.id || 0
          newList.push(equipmentsNotConnectedResult.data[index])
        }
        formRef?.current?.setFieldValue("equipments", newList)
        setAddedEquipment(null)
      }
    }
  }, [addedEquipment, equipmentsNotConnectedResult?.data, equipmentsNotConnectedResult.status])

  return (
    <>
      <Formik
        innerRef={formRef}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnChange={false}
        validationSchema={deviceValidationSchema}>
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          handleSubmit,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container direction="row" spacing={2}>
              <Grid item md={4}>
                <PaperBox>
                  <Grid
                    container
                    spacing={2}
                    marginBottom={2}
                    justifyContent={"space-between"}
                    alignItems="center">
                    <Grid item xs={7}>
                      <Typography variant="h4">{t("global.equipment", "Equipment")}</Typography>
                    </Grid>
                    <Grid item xs={5} textAlign="right">
                      <Button
                        disabled={equipmentsNotConnectedResult.status !== 2}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          appContext.toggleSideBar("equipment", true)
                        }}>
                        {t("global.add", "Add")}
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} marginBottom={2}>
                    <Grid item xs={12} md={6}>
                      {equipmentsNotConnectedResult.status === 2 && equipmentsNotConnectedResult.data && (
                        <FormControl fullWidth>
                          <InputLabel id="group-select-label" size="small">
                            {t("global.equipment_type", "Equipment type")}
                          </InputLabel>
                          <Select
                            size="small"
                            labelId="group-select-label"
                            id="group-select"
                            value={selectedGroup}
                            label={t("global.group", "Group")}
                            onChange={(e) => setSelectedGroup(e.target.value)}>
                            <MenuItem value="">{t("global.all", "All")}</MenuItem>
                            {Object.keys(groupedData(equipmentsNotConnectedResult.data)).map((group) => (
                              <MenuItem key={group} value={group}>
                                {group}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <TextField
                          size="small"
                          autoComplete="off"
                          fullWidth
                          id="search"
                          label={t("global.search", "Search")}
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          onBlur={handleBlur}
                          variant="outlined"
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      {equipmentsNotConnectedResult.status === 2 &&
                        equipmentsNotConnectedResult.data &&
                        Object.entries(
                          groupedData(
                            equipmentsNotConnectedResult.data.filter(
                              (item) =>
                                (!searchValue ||
                                  (item.equipmentId &&
                                    item.equipmentId.toLowerCase().includes(searchValue.toLowerCase()))) &&
                                (!selectedGroup || item.equipmentType === selectedGroup)
                            )
                          )
                        ).map(([group, items]) => (
                          <FieldArray name="equipments" key={group}>
                            {({ push, remove, form }) => (
                              <>
                                <Box mb={2}>
                                  {group !== "null" ? (
                                    <Typography variant="h5" marginBottom={1}>
                                      {group}
                                    </Typography>
                                  ) : (
                                    <Divider />
                                  )}
                                  {items.map((equipment, index) => {
                                    const selectedIndex = form.values?.equipments?.findIndex(
                                      (item) =>
                                        item.equipmentId === equipment.equipmentId &&
                                        item.mountedOnDeviceId !== null
                                    )
                                    const isSelected = selectedIndex !== -1 && selectedIndex !== undefined
                                    return (
                                      <Box style={{ marginY: 15 }} key={equipment.equipmentId || index}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={isSelected}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  if (!isSelected) {
                                                    const equipmentToAdd = {
                                                      ...equipment,
                                                      mountedOnDeviceId: values.id || 0
                                                    }
                                                    push(equipmentToAdd)
                                                  }
                                                } else {
                                                  if (isSelected) {
                                                    setFieldValue(
                                                      `equipments[${selectedIndex}].positionPriority`,
                                                      false
                                                    )
                                                    setFieldValue(
                                                      `equipments[${selectedIndex}].ioPriority`,
                                                      false
                                                    )
                                                    remove(selectedIndex)
                                                  }
                                                }
                                              }}
                                            />
                                          }
                                          label={equipment.equipmentId}
                                        />
                                      </Box>
                                    )
                                  })}
                                </Box>
                              </>
                            )}
                          </FieldArray>
                        ))}
                    </Grid>
                  </Grid>
                </PaperBox>
              </Grid>
              <Grid item md={8}>
                <PaperBox>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            onChange={(item) => {
                              const checked = item.target.checked
                              setFieldValue("isActive", checked)
                            }}
                            checked={values.isActive}
                          />
                        }
                        label={t("global.active", "Active")}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id="internalDeviceId"
                        label={t("global.internal_number", "Internal number")}
                        value={values.internalDeviceId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="outlined"
                        error={touched.internalDeviceId && errors.internalDeviceId ? true : false}
                        helperText={
                          touched.internalDeviceId && errors.internalDeviceId ? errors.internalDeviceId : null
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id="deviceId"
                        label={t("deviation.license_plate", "License plate")}
                        value={values.deviceId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.deviceId && errors.deviceId ? true : false}
                        helperText={touched.deviceId && errors.deviceId ? errors.deviceId : null}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {technicalPackagesResult.status === 2 && (
                        <Autocomplete
                          style={{ minWidth: "100%" }}
                          id="technicalPackageId"
                          options={technicalPackagesResult.data}
                          getOptionLabel={(option) => (option.name ? option.name : "")}
                          value={
                            technicalPackagesResult.data.find((i) => i.id === values.technicalPackageId) || ""
                          }
                          isOptionEqualToValue={(option, value) => {
                            return option.id === value.id
                          }}
                          onChange={(e, value) => {
                            setFieldValue("technicalPackageId", value?.id)
                          }}
                          renderInput={(params) => (
                            <TextField
                              style={{ background: theme.palette.background.paper }}
                              {...params}
                              label={t("sysadmin.select_package", "Select package")}
                              variant="outlined"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.technicalPackageId && errors.technicalPackageId ? true : false}
                              helperText={
                                touched.technicalPackageId && errors.technicalPackageId
                                  ? errors.technicalPackageId
                                  : null
                              }
                            />
                          )}
                        />
                      )}
                      <FormControl sx={{ width: "100%", marginTop: 2 }}>
                        <InputLabel id="serviceLevel-label">
                          {t("sysadmin.service_level", "Service level")}
                        </InputLabel>
                        <Select
                          sx={{ width: "100%" }}
                          className="w-100"
                          variant="outlined"
                          labelId="serviceLevel-label"
                          id="serviceLevel"
                          label={t("sysadmin.service_level", "Service level")}
                          value={values.serviceLevel}
                          onChange={(event) => {
                            const value = event.target.value
                            setFieldValue("serviceLevel", value)
                          }}>
                          {serviceLevels &&
                            serviceLevels.map((item) => {
                              return <MenuItem value={item.id}>{item.name}</MenuItem>
                            })}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        autoComplete="off"
                        minRows={4}
                        multiline
                        fullWidth
                        id="deviceDescription"
                        label={t("global.description", "Description")}
                        value={values.deviceDescription}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.deviceDescription && errors.deviceDescription ? true : false}
                        helperText={
                          touched.deviceDescription && errors.deviceDescription
                            ? errors.deviceDescription
                            : null
                        }
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <FormControl>
                        <FormLabel id="simCardSupplier">
                          {t("sysadmin.sim_card_provider", "SIM card provider")}
                        </FormLabel>
                        <RadioGroup
                          row
                          aria-label="simCardSupplier"
                          name="simCardSupplier"
                          value={values.simCardSupplier}
                          onChange={(e) => {
                            const value = e.target.value === "" ? null : Number.parseInt(e.target.value)
                            setFieldValue("simCardSupplier", value)
                          }}>
                          <FormControlLabel
                            value={null}
                            control={<Radio />}
                            label={t("global.none", "None")}
                          />
                          <FormControlLabel
                            value={0}
                            control={<Radio />}
                            label={t("sysadmin.malux", "Malux")}
                          />
                          <FormControlLabel value={1} control={<Radio />} label={t("global.own", "Own")} />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id="phone"
                        label={t("settings.form.phone", "Phone")}
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="outlined"
                        error={touched.phone && errors.phone ? true : false}
                        helperText={touched.phone && errors.phone ? errors.phone : null}
                      />
                    </Grid>
                  </Grid>
                </PaperBox>
                <PaperBox>
                  {values.equipments && values.equipments.length > 0 ? (
                    <>
                      <Grid container spacing={2} marginBottom={1}>
                        <Grid item xs={3}>
                          <Typography variant="labelSmallBold" color="textSecondary">
                            {t("global.equipment", "Equipment")}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="labelSmallBold" color="textSecondary">
                            {t("global.coupling", "Coupling")}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="labelSmallBold" color="textSecondary">
                            {t("sysadmin.position_prio", "Position Priority")}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="labelSmallBold" color="textSecondary">
                            {t("sysadmin.io_prio", "IO Priority")}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Divider />

                      {errors.equipments && typeof errors.equipments === "string" && (
                        <Typography variant="bodySmall" color="error" sx={{ marginTop: 1 }}>
                          {errors.equipments}
                        </Typography>
                      )}

                      <FieldArray name="equipments">
                        {({ push, remove, form }) => (
                          <>
                            {form.values.equipments &&
                              form.values.equipments
                                .filter((equipment) => equipment.mountedOnDeviceId !== null)
                                .map((equipment, index) => {
                                  const equipmentIndex = form.values.equipments.findIndex(
                                    (item) => item.equipmentId === equipment.equipmentId
                                  )

                                  return (
                                    <>
                                      <Grid
                                        container
                                        spacing={2}
                                        alignItems="center"
                                        key={equipment.id || equipmentIndex}>
                                        <Grid item xs={3}>
                                          <Typography variant="bodyMedium">
                                            {equipment.equipmentType}
                                          </Typography>
                                          {equipment?.supplements?.map((data, index) => (
                                            <>
                                              <Typography variant="bodySmall" color="textSecondary">
                                                {data.alarmType === 2
                                                  ? t("global.immobilizer", "Immobilizer")
                                                  : t("vehiclealarm.alcohol_lock", "Alcohol lock")}{" "}
                                                - {data.avlId}
                                              </Typography>
                                            </>
                                          ))}
                                        </Grid>
                                        <Grid item xs={3}>
                                          <Typography variant="bodyMedium">
                                            {equipment.equipmentId}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={!!equipment.positionPriority}
                                                onChange={(e) => {
                                                  form.setFieldValue(
                                                    `equipments[${equipmentIndex}].positionPriority`,
                                                    e.target.checked
                                                  )
                                                }}
                                              />
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={2}>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={!!equipment.ioPriority}
                                                onChange={(e) => {
                                                  form.setFieldValue(
                                                    `equipments[${equipmentIndex}].ioPriority`,
                                                    e.target.checked
                                                  )
                                                }}
                                              />
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={1} justifyContent={"end"} display="flex">
                                          <FontAwesomeIcon
                                            icon={faTrashCanXmark}
                                            color={theme.palette.error.main}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => {
                                              form.setFieldValue(
                                                `equipments[${index}].positionPriority`,
                                                false
                                              )
                                              form.setFieldValue(`equipments[${index}].ioPriority`, false)
                                              form.setFieldValue(
                                                `equipments[${index}].mountedOnDeviceId`,
                                                null
                                              )
                                            }}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Divider style={{ marginTop: 5, marginBottom: 5 }} />
                                    </>
                                  )
                                })}
                          </>
                        )}
                      </FieldArray>
                    </>
                  ) : (
                    <Grid container justifyContent="center" alignItems="center" direction="column" gap={4}>
                      <Typography variant="bodySmall">None equipments are selected</Typography>
                    </Grid>
                  )}
                </PaperBox>
              </Grid>
            </Grid>
            <Box marginTop={15}></Box>
            <Box sx={{ position: "fixed", bottom: 8, left: 0, right: 15 }} elevation={3}>
              <BottomNavigation
                sx={{ justifyContent: "flex-end", backgroundColor: "transparent", height: "auto" }}>
                <Button color="primary" type="submit" result={result} feedback={feedback}>
                  {editMode ? t("global.save", "Save") : t("global.add", "Add")}
                </Button>
              </BottomNavigation>
            </Box>
          </form>
        )}
      </Formik>
      <SidebarEquipmentForm onSubmit={handleEquipmentSubmit} result={addEquipmentResult} />
    </>
  )
}
